// Global Instructions Rule Applied! - 2026-04-01

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import Button from '../../../core/components/Button.jsx'
import { InputManager } from '../../../core/components/InputManager/index.ts'
import { FieldTypeEnum } from '../../../core/components/InputManager/types.ts'
import type { FieldConfig } from '../../../core/components/InputManager/types.ts'
import ViewToolbar from '../../../core/components/ViewToolbar/index.tsx'
import ExportToolbarButton from '../../../core/components/ViewToolbar/parts/ExportToolbarButton.tsx'
import { ViewConfig } from '../../../core/components/ViewConfig/index.ts'
import type { ViewConfigColumn } from '../../../core/components/ViewConfig/types.ts'
import { useMenuItemUserPreference } from '../../global_configs/useMenuItemUserPreference.ts'
import { PREF_TABLE_COLUMN_KEYS } from '../../global_configs/tableViewPreferences.ts'
import { useScheduleOfInvestmentsData } from '../hooks/useScheduleOfInvestmentsData.ts'
import { useCashFlowsData } from '../hooks/useCashFlowsData.ts'
import { ScheduleOfInvestmentsTable } from './ScheduleOfInvestmentsTable.tsx'
import { CashFlowsTable } from './CashFlowsTable.tsx'
import type { UserPreferenceReportModel } from '../../../state_manager/data_models/user_preference_report_model.ts'
import type { ReportingConfigModel } from '../../../state_manager/data_models/reporting_config_model.ts'
import type { AppDispatch, RootState } from '../../../state_manager/store.ts'
import {
  mergeUserPreferenceRowData,
  removeUserPreferenceForMenuItem
} from '../../../state_manager/state_update_actions/global_configs/actions.ts'
import {
  MENU_ITEM_KEY_DOWNLOADS,
  REPORT_CONFIG_NAME_SCHEDULE_OF_INVESTMENTS,
  REPORT_CONFIG_NAME_CASH_FLOWS,
  TABLE_KEY_SCHEDULE_OF_INVESTMENTS,
  TABLE_KEY_CASH_FLOWS,
  UUID_REGEX
} from '../../../state_manager/globals.ts'
import { fetchGlobalConfigsThunk } from '../../global_configs/controller.ts'
import { useReportingGridColumnsWithPreferences } from '../../../state_manager/hooks/useReportingGridColumnsWithPreferences.ts'
import { getReportingGridColumnsForReportId } from '../../../state_manager/utils/reportingGridColumns.ts'
import { exportJsonToExcelBranded } from '../../../core/services/utilitiesService.ts'
import {
  buildAsOfLabel,
  buildBrandedExportPayload,
  buildDownloadsSubtitle
} from '../utils/brandedExport.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'

const EMPTY_FUNDS: Array<{ id?: string; name?: string; managed_by_us?: boolean }> = []

type DownloadsFormState = {
  reportType: string
  fundIds: string[]
  date: string
  pByFund: boolean
  pByVintage: boolean
  pByTranche: boolean
}

type AppliedSnapshot = DownloadsFormState

function resolveReportIdFromPreference(
  data: Record<string, unknown>,
  reportsForMenu: ReportingConfigModel[],
  defaultReportId: string | undefined
): string {
  const raw = data.report_type
  if (typeof raw === 'string') {
    if (UUID_REGEX.test(raw) && reportsForMenu.some((r) => r.id === raw)) {
      return raw
    }
    if (raw === TABLE_KEY_SCHEDULE_OF_INVESTMENTS) {
      const soi = reportsForMenu.find((r) => r.name === REPORT_CONFIG_NAME_SCHEDULE_OF_INVESTMENTS)
      if (soi) return soi.id
    }
    if (raw === TABLE_KEY_CASH_FLOWS) {
      const cf = reportsForMenu.find((r) => r.name === REPORT_CONFIG_NAME_CASH_FLOWS)
      if (cf) return cf.id
    }
  }
  return defaultReportId ?? ''
}

function readPreferenceBool(data: Record<string, unknown>, key: string, defaultValue: boolean): boolean {
  return typeof data[key] === 'boolean' ? (data[key] as boolean) : defaultValue
}

function buildDownloadsFormState(
  row: UserPreferenceReportModel | undefined,
  reportsForMenu: ReportingConfigModel[],
  defaultReportId: string | undefined
): DownloadsFormState {
  const data = (row?.data ?? {}) as Record<string, unknown>
  const fundList = data.fund_ids
  return {
    reportType: resolveReportIdFromPreference(data, reportsForMenu, defaultReportId),
    fundIds: Array.isArray(fundList) ? fundList.filter((id): id is string => typeof id === 'string') : [],
    date: typeof data.date === 'string' && data.date ? data.date : dayjs().endOf('day').format('YYYY-MM-DD'),
    pByFund: readPreferenceBool(data, 'p_by_fund', true),
    pByVintage: readPreferenceBool(data, 'p_by_vintage', true),
    pByTranche: readPreferenceBool(data, 'p_by_tranche', false)
  }
}

function fundIdsEqual(a: string[], b: string[]) {
  if (a.length !== b.length) return false
  const sa = [...a].sort()
  const sb = [...b].sort()
  return sa.every((v, i) => v === sb[i])
}

function downloadsFormStateEqual(a: DownloadsFormState, b: DownloadsFormState): boolean {
  return (
    a.reportType === b.reportType &&
    fundIdsEqual(a.fundIds, b.fundIds) &&
    a.date === b.date &&
    a.pByFund === b.pByFund &&
    a.pByVintage === b.pByVintage &&
    a.pByTranche === b.pByTranche
  )
}

// Schedule of Investments lets the user toggle the fund / vintage / tranche
// breakout; hide the matching grid columns when a breakout is off. Other
// Downloads reports (e.g. Cash Flows) show every column they define.
function filterSoiColumns(
  configs: ViewConfigColumn[],
  showByFund: boolean,
  showByVintage: boolean,
  showByTranche: boolean
): ViewConfigColumn[] {
  return configs.filter((c) => {
    if (c.valueKey === 'funds__name') return showByFund
    if (c.valueKey === 'vintage__label') return showByVintage
    if (c.valueKey === 'tranches__name') return showByTranche
    return true
  })
}

export function DownloadsForm() {
  const dispatch = useDispatch<AppDispatch>()
  const { menuItemId, preferenceRow, menuItems, persistPreference, deletePreference } =
    useMenuItemUserPreference(MENU_ITEM_KEY_DOWNLOADS)

  const reportingConfigs = useSelector((state: RootState) => state.globalConfigs?.reporting_configs ?? [])
  const reportingColumnDefaults = useSelector(
    (state: RootState) => state.globalConfigs?.reporting_column_defaults ?? []
  )
  const reportingColumns = useSelector((state: RootState) => state.globalConfigs?.reporting_columns ?? [])

  const reportsForMenu = useMemo(
    () => (menuItemId ? reportingConfigs.filter((r) => r.menu_item_id === menuItemId) : []),
    [reportingConfigs, menuItemId]
  )

  const defaultReportId = useMemo(() => {
    const marked = reportsForMenu.find((r) => r.default === true)
    return marked?.id ?? reportsForMenu[0]?.id
  }, [reportsForMenu])

  const reportTypeOptions = useMemo(
    () => reportsForMenu.map((r) => ({ value: r.id, label: r.name, name: r.name })),
    [reportsForMenu]
  )

  const defaults = useMemo(
    () => buildDownloadsFormState(preferenceRow, reportsForMenu, defaultReportId),
    [preferenceRow, reportsForMenu, defaultReportId]
  )

  const [applied, setApplied] = useState<AppliedSnapshot | null>(null)
  const [draft, setDraft] = useState<DownloadsFormState | null>(null)
  const [inputManagerKey, setInputManagerKey] = useState(0)

  const configsReady = menuItems.length > 0

  useEffect(() => {
    if (!configsReady) return
    const d = buildDownloadsFormState(preferenceRow, reportsForMenu, defaultReportId)
    setApplied((prev) => prev ?? { ...d })
    setDraft((prev) => prev ?? d)
  }, [configsReady, preferenceRow, reportsForMenu, defaultReportId])

  const formState = draft ?? defaults
  const reportType = formState.reportType
  const selectedFundIds = formState.fundIds
  const selectedDate = formState.date
  const pByFund = formState.pByFund
  const pByVintage = formState.pByVintage
  const pByTranche = formState.pByTranche

  const selectedReport = useMemo(() => reportsForMenu.find((r) => r.id === reportType), [reportsForMenu, reportType])

  const isSoiReport = selectedReport?.name === REPORT_CONFIG_NAME_SCHEDULE_OF_INVESTMENTS
  const isCashFlowsReport = selectedReport?.name === REPORT_CONFIG_NAME_CASH_FLOWS
  // Both report kinds are driven by a fund selection + a report date.
  const showFundsDateFields = isSoiReport || isCashFlowsReport
  // Reports that render a grid + export toolbar below the form inputs.
  const showReportUi = isSoiReport || isCashFlowsReport

  const dirty = useMemo(() => {
    if (!applied || !draft) return false
    return (
      draft.reportType !== applied.reportType ||
      !fundIdsEqual(draft.fundIds, applied.fundIds) ||
      draft.date !== applied.date ||
      draft.pByFund !== applied.pByFund ||
      draft.pByVintage !== applied.pByVintage ||
      draft.pByTranche !== applied.pByTranche
    )
  }, [applied, draft])

  const systemDefaults = useMemo(
    () => buildDownloadsFormState(undefined, reportsForMenu, defaultReportId),
    [reportsForMenu, defaultReportId]
  )

  const appliedDiffersFromSystemDefault = applied !== null && !downloadsFormStateEqual(applied, systemDefaults)

  const showFooterBar = dirty || appliedDiffersFromSystemDefault

  const persistData = useCallback(
    (next: DownloadsFormState) => {
      if (!menuItemId) return
      const merged = {
        ...(preferenceRow?.data ?? {}),
        report_type: next.reportType,
        fund_ids: next.fundIds,
        date: next.date,
        p_by_fund: next.pByFund,
        p_by_vintage: next.pByVintage,
        p_by_tranche: next.pByTranche
      }
      persistPreference(next.reportType, merged)
        .then((result) => {
          if (!result.success) return
          dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        })
        .catch((err) => console.error('Failed to persist downloads preferences:', err))
    },
    [menuItemId, persistPreference, preferenceRow?.data, dispatch]
  )

  const funds = useSelector(
    (state: { globalConfigs?: { funds?: Array<{ id?: string; name?: string; managed_by_us?: boolean }> } }) =>
      state.globalConfigs?.funds ?? EMPTY_FUNDS
  )

  const fundOptions = useMemo(() => {
    return funds
      .filter((fund) => fund.id && fund.name && fund.managed_by_us === true)
      .map((fund) => ({
        value: fund.id as string,
        label: fund.name as string,
        name: fund.name as string
      }))
  }, [funds])

  const fetchEnabled = configsReady && applied !== null

  const appliedReportForFetch = useMemo(
    () => reportsForMenu.find((r) => r.id === applied?.reportType),
    [reportsForMenu, applied?.reportType]
  )
  const appliedReportName = appliedReportForFetch?.name

  const shouldFetchSoi = fetchEnabled && appliedReportName === REPORT_CONFIG_NAME_SCHEDULE_OF_INVESTMENTS
  const shouldFetchCashFlows = fetchEnabled && appliedReportName === REPORT_CONFIG_NAME_CASH_FLOWS

  const { data: soiData, loading: soiLoading, refetch: refetchSoiData } = useScheduleOfInvestmentsData(
    applied?.fundIds ?? defaults.fundIds,
    applied?.date ?? defaults.date,
    applied?.pByFund ?? defaults.pByFund,
    applied?.pByVintage ?? defaults.pByVintage,
    applied?.pByTranche ?? defaults.pByTranche,
    {
      enabled: shouldFetchSoi
    }
  )

  const { data: cashFlowsData, loading: cashFlowsLoading, refetch: refetchCashFlowsData } = useCashFlowsData(
    applied?.fundIds ?? defaults.fundIds,
    applied?.date ?? defaults.date,
    {
      enabled: shouldFetchCashFlows
    }
  )

  // Active report data (the report whose results are currently displayed).
  const activeData = isCashFlowsReport ? cashFlowsData : soiData
  const activeLoading = isCashFlowsReport ? cashFlowsLoading : soiLoading
  const refetchActiveData = isCashFlowsReport ? refetchCashFlowsData : refetchSoiData
  const activeReportTitle = isCashFlowsReport ? 'Cash Flows' : 'Schedule of Investments'
  const activeModuleKey = isCashFlowsReport ? TABLE_KEY_CASH_FLOWS : TABLE_KEY_SCHEDULE_OF_INVESTMENTS
  const activeRecordTitleField = isCashFlowsReport ? 'up_tranches__name' : 'accounts__name'

  const gridColumnsWithPreferences = useReportingGridColumnsWithPreferences(reportType, preferenceRow)

  // SOI hides breakout columns per its checkboxes; other reports show all columns.
  const activeGridColumns = useMemo(
    () =>
      isCashFlowsReport
        ? gridColumnsWithPreferences
        : filterSoiColumns(
            gridColumnsWithPreferences,
            applied?.pByFund ?? defaults.pByFund,
            applied?.pByVintage ?? defaults.pByVintage,
            applied?.pByTranche ?? defaults.pByTranche
          ),
    [gridColumnsWithPreferences, isCashFlowsReport, applied, defaults]
  )

  const allReportColumns = useMemo(() => {
    if (!reportType) return []
    const rows = getReportingGridColumnsForReportId(reportType, {
      reporting_configs: reportingConfigs,
      reporting_column_defaults: reportingColumnDefaults,
      reporting_columns: reportingColumns
    })
    if (isCashFlowsReport) return rows
    return filterSoiColumns(
      rows,
      applied?.pByFund ?? defaults.pByFund,
      applied?.pByVintage ?? defaults.pByVintage,
      applied?.pByTranche ?? defaults.pByTranche
    )
  }, [reportType, reportingConfigs, reportingColumnDefaults, reportingColumns, isCashFlowsReport, applied, defaults])

  const addColumnsCatalog = useMemo(
    () => ({
      deal: allReportColumns,
      related: [] as ViewConfigColumn[]
    }),
    [allReportColumns]
  )

  const catalogFieldNameOrder = useMemo(
    () => [...addColumnsCatalog.deal, ...addColumnsCatalog.related].map((c) => c.valueKey),
    [addColumnsCatalog]
  )

  const defaultColumnKeys = useMemo(() => {
    return allReportColumns.map((r) => r.valueKey)
  }, [allReportColumns])

  const columnByValueKey = useMemo(() => {
    const map = new Map<string, ViewConfigColumn>()
    for (const c of addColumnsCatalog.deal) map.set(c.valueKey, c)
    for (const c of addColumnsCatalog.related) map.set(c.valueKey, c)
    for (const c of activeGridColumns) {
      if (!map.has(c.valueKey)) map.set(c.valueKey, c)
    }
    return map
  }, [addColumnsCatalog, activeGridColumns])

  const resolveViewConfigColumn = useCallback(
    (valueKey: string) => columnByValueKey.get(valueKey),
    [columnByValueKey]
  )

  const mainFields: FieldConfig[] = useMemo(() => {
    const baseFields: FieldConfig[] = [
      {
        key: 'reportType',
        label: 'Report Type',
        type: FieldTypeEnum.SELECT,
        value: reportType,
        options: reportTypeOptions,
        editable: true
      }
    ]

    if (showFundsDateFields) {
      baseFields.push(
        {
          key: 'funds',
          label: 'Funds',
          type: FieldTypeEnum.SELECT,
          value: selectedFundIds,
          options: fundOptions,
          mode: 'multiple',
          editable: true,
          tagDisplay: true,
          placeholder: 'Select funds'
        },
        {
          key: 'date',
          label: 'Date',
          type: FieldTypeEnum.DATE,
          value: selectedDate,
          format: 'YYYY-MM-DD',
          editable: true
        }
      )
    }

    return baseFields
  }, [
    reportType,
    reportTypeOptions,
    showFundsDateFields,
    selectedFundIds,
    selectedDate,
    fundOptions
  ])

  const soiCheckboxFields: FieldConfig[] = useMemo(
    () => [
      {
        key: 'pByFund',
        label: '',
        type: FieldTypeEnum.CHECKBOX,
        value: pByFund,
        checkboxLabel: 'Funds',
        editable: true,
        className: 'mb-0 shrink-0',
        labelPosition: 'none'
      },
      {
        key: 'pByVintage',
        label: '',
        type: FieldTypeEnum.CHECKBOX,
        value: pByVintage,
        checkboxLabel: 'Vintages',
        editable: true,
        className: 'mb-0 shrink-0',
        labelPosition: 'none'
      },
      {
        key: 'pByTranche',
        label: '',
        type: FieldTypeEnum.CHECKBOX,
        value: pByTranche,
        checkboxLabel: 'Tranches',
        editable: true,
        className: 'mb-0 shrink-0',
        labelPosition: 'none'
      }
    ],
    [pByFund, pByVintage, pByTranche]
  )

  const handleFieldChange = (field: string, value: unknown) => {
    const base = draft ?? defaults
    let next: DownloadsFormState

    if (field === 'reportType') {
      next = { ...base, reportType: value as string }
    } else if (field === 'funds') {
      const fundNext = Array.isArray(value) ? (value as string[]) : []
      next = { ...base, fundIds: fundNext }
    } else if (field === 'pByFund') {
      next = { ...base, pByFund: value as boolean }
    } else if (field === 'pByVintage') {
      next = { ...base, pByVintage: value as boolean }
    } else if (field === 'pByTranche') {
      next = { ...base, pByTranche: value as boolean }
    } else if (field === 'date') {
      const dateStr = value as string
      const dateNext = dateStr
        ? dayjs(dateStr).endOf('day').format('YYYY-MM-DD')
        : dayjs().endOf('day').format('YYYY-MM-DD')
      next = { ...base, date: dateNext }
    } else {
      return
    }

    setDraft(next)
  }

  const handleApply = useCallback(() => {
    if (!draft || !applied) return
    persistData(draft)
    setApplied({
      reportType: draft.reportType,
      fundIds: [...draft.fundIds],
      date: draft.date,
      pByFund: draft.pByFund,
      pByVintage: draft.pByVintage,
      pByTranche: draft.pByTranche
    })
  }, [draft, applied, persistData])

  const handleCancel = useCallback(() => {
    if (!applied || !draft) return
    setDraft({
      reportType: applied.reportType,
      fundIds: [...applied.fundIds],
      date: applied.date,
      pByFund: applied.pByFund,
      pByVintage: applied.pByVintage,
      pByTranche: applied.pByTranche
    })
    setInputManagerKey((k) => k + 1)
  }, [applied, draft])

  const handleReset = useCallback(() => {
    const clean = buildDownloadsFormState(undefined, reportsForMenu, defaultReportId)
    if (!menuItemId) {
      setDraft(clean)
      setApplied({ ...clean })
      setInputManagerKey((k) => k + 1)
      return
    }
    void (async () => {
      const result = await deletePreference()
      if (!result.success) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to delete downloads preferences:', result.error)
        }
        return
      }
      dispatch(removeUserPreferenceForMenuItem(menuItemId))
      setDraft(clean)
      setApplied({ ...clean })
      setInputManagerKey((k) => k + 1)
    })()
  }, [menuItemId, deletePreference, dispatch, reportsForMenu, defaultReportId])

  const persistColumnFieldOrder = useCallback(
    async (fieldNames: string[]) => {
      if (!menuItemId) return { success: false as const, error: 'No menu item' }
      const hadExistingRow = Boolean(preferenceRow)
      const merged: Record<string, unknown> = {
        ...(preferenceRow?.data ?? {}),
        [PREF_TABLE_COLUMN_KEYS]: fieldNames,
        report_type: applied?.reportType ?? defaults.reportType,
        fund_ids: applied?.fundIds ?? defaults.fundIds,
        date: applied?.date ?? defaults.date,
        p_by_fund: applied?.pByFund ?? defaults.pByFund,
        p_by_vintage: applied?.pByVintage ?? defaults.pByVintage,
        p_by_tranche: applied?.pByTranche ?? defaults.pByTranche
      }
      const result = await persistPreference(reportType || undefined, merged)
      if (result.success) {
        if (hadExistingRow) {
          dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        } else {
          const user = store.getState().auth.user
          if (user) {
            await fetchGlobalConfigsThunk(user)(dispatch)
          }
        }
      }
      return result
    },
    [menuItemId, preferenceRow, applied, defaults, persistPreference, reportType, dispatch]
  )

  const resetColumnLayoutToDefault = useCallback(async () => {
    if (!menuItemId) return { success: false as const, error: 'No menu item' }
    const hadExistingRow = Boolean(preferenceRow)
    const merged: Record<string, unknown> = {
      ...(preferenceRow?.data ?? {}),
      report_type: applied?.reportType ?? defaults.reportType,
      fund_ids: applied?.fundIds ?? defaults.fundIds,
      date: applied?.date ?? defaults.date,
      p_by_fund: applied?.pByFund ?? defaults.pByFund,
      p_by_vintage: applied?.pByVintage ?? defaults.pByVintage,
      p_by_tranche: applied?.pByTranche ?? defaults.pByTranche
    }
    delete merged[PREF_TABLE_COLUMN_KEYS]
    const result = await persistPreference(reportType || undefined, merged)
    if (result.success) {
      if (hadExistingRow) {
        dispatch(mergeUserPreferenceRowData(menuItemId, merged))
      } else {
        const user = store.getState().auth.user
        if (user) {
          await fetchGlobalConfigsThunk(user)(dispatch)
        }
      }
    }
    return result
  }, [menuItemId, preferenceRow, applied, defaults, persistPreference, reportType, dispatch])

  const columnLayout = useMemo(
    () => ({
      onPersistFieldOrder: persistColumnFieldOrder,
      onResetToDefaults: resetColumnLayoutToDefault,
      resolveColumn: resolveViewConfigColumn,
      catalogFieldNameOrder: catalogFieldNameOrder,
      defaultTableColumnKeys: defaultColumnKeys
    }),
    [
      persistColumnFieldOrder,
      resetColumnLayoutToDefault,
      resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultColumnKeys
    ]
  )

  const [exporting, setExporting] = useState(false)

  const handleExport = useCallback(async () => {
    if (activeData.length === 0) return
    setExporting(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const columnsForExport = activeGridColumns.filter(
        (c) => c.fieldType !== FieldTypeEnum.TASKS && c.fieldType !== 'tasks'
      )

      // Downloads-screen reports are presentation documents, not grid dumps —
      // both export in the firm's report design system. The breakout clause in
      // the subtitle is an SOI concept; Cash Flows shows the fund selection
      // alone.
      const asOfDate = applied?.date ?? defaults.date
      const selectedFundNames = (applied?.fundIds ?? defaults.fundIds)
        .map((id) => fundOptions.find((o) => o.value === id)?.label)
        .filter((name): name is string => Boolean(name))
      const filename = `${activeReportTitle} - ${asOfDate}.xlsx`
      const payload = buildBrandedExportPayload(columnsForExport, activeData)

      const result = await exportJsonToExcelBranded(
        {
          ...payload,
          meta: {
            report_title: activeReportTitle,
            as_of_label: buildAsOfLabel(dayjs(asOfDate).format('MMMM D, YYYY')),
            subtitle: buildDownloadsSubtitle(
              selectedFundNames,
              isSoiReport
                ? {
                    byFund: applied?.pByFund ?? defaults.pByFund,
                    byVintage: applied?.pByVintage ?? defaults.pByVintage,
                    byTranche: applied?.pByTranche ?? defaults.pByTranche
                  }
                : undefined
            )
          },
          filename
        },
        token
      )
      if (result.success) {
        toast.success(`${filename} exported successfully`)
      } else {
        showErrorToast(result.error || 'Export failed')
      }
    } finally {
      setExporting(false)
    }
  }, [activeData, activeGridColumns, activeReportTitle, isSoiReport, applied, defaults, fundOptions])

  return (
    <div className='flex flex-1 min-h-0 flex-col overflow-hidden'>
      {showReportUi && (
        <ViewToolbar
          moduleKey={activeModuleKey}
          recordTitleField={activeRecordTitleField}
          searchEnabled={false}
          addButtonEnabled={false}
          activeFilters={[]}
          onRefreshData={() => refetchActiveData()}
          beforeAddSlot={
            <div className='flex items-center gap-2'>
              <ViewConfig
                moduleTitle={activeReportTitle}
                columns={activeGridColumns}
                showFilteringButton={false}
                showSortingButton={false}
                addColumnsCatalog={addColumnsCatalog}
                addColumnsTabLabels={{ primary: activeReportTitle, related: 'Related' }}
                columnLayout={columnLayout}
              />
              <ExportToolbarButton
                onClick={handleExport}
                loading={exporting}
                disabled={activeData.length === 0}
              />
            </div>
          }
        />
      )}
      <FormSection title='Downloads' scrollable={false} className='flex-1 min-h-0 flex flex-col overflow-hidden'>
        <div className='flex flex-col h-full min-h-0'>
          <div className='max-w-2xl flex-shrink-0 mt-1'>
            <InputManager
              key={`${inputManagerKey}-main`}
              inputMode='edit'
              fields={mainFields}
              onChange={handleFieldChange}
            />
            {isSoiReport && (
              <InputManager
                key={`${inputManagerKey}-soi-checks`}
                inputMode='edit'
                fields={soiCheckboxFields}
                onChange={handleFieldChange}
                className='flex flex-wrap items-center gap-x-6 gap-y-2 mt-2'
              />
            )}
            {showFooterBar && (
              <div className='flex flex-wrap items-center justify-end gap-2 pt-1.5 mt-3 border-t border-gray-200 mb-4'>
                {appliedDiffersFromSystemDefault && (
                  <Button variant='edit' size='small' onClick={handleReset}>
                    Reset
                  </Button>
                )}
                {dirty && (
                  <>
                    <Button variant='cancel' size='small' onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button variant='save' size='small' onClick={handleApply}>
                      Apply
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
          {showReportUi && (
            <div className='flex-1 min-h-0 mt-1'>
              {isCashFlowsReport ? (
                <CashFlowsTable data={activeData} loading={activeLoading} gridColumns={activeGridColumns} />
              ) : (
                <ScheduleOfInvestmentsTable data={activeData} loading={activeLoading} gridColumns={activeGridColumns} />
              )}
            </div>
          )}
        </div>
      </FormSection>
    </div>
  )
}
